/**
 * Mission sub-KPI filter helpers (DEV-822).
 *
 * ─────────────────────────────────────────────────────────────────────
 *  PRODUCT PRINCIPLE — DO NOT "FIX" THIS BY MOVING HSE TO ITS OWN CATEGORY
 * ─────────────────────────────────────────────────────────────────────
 * HSE Incident Tracker sub-KPIs (mission_type_key='incident_count') live
 * under category='mission' INTENTIONALLY. This is a deliberate product
 * decision, not a storage shortcut, and it is NON-NEGOTIABLE.
 *
 * Why: Life4Earth serves teenagers, parents, and teachers. The platform
 * teaches that Health, Environment, and Safety are core to any project's
 * mission — not separate compliance checkboxes that happen alongside the
 * "real" work. Filing HSE under its own category (e.g., 'safety_compliance'
 * or 'event_log') would visually relegate it to ops paperwork and
 * undermine the educational message that safety IS part of mission.
 *
 * The right architectural pattern is what you see here: HSE stays in
 * Mission category, but is unscored — the reliability engine doesn't
 * measure HSE timeliness/completeness because incidents are event-driven
 * and ideal=zero, neither of which fits a scoring engine. Incidents are
 * escalated and surfaced in the project Performance Summary instead.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  HOW TO USE
 * ─────────────────────────────────────────────────────────────────────
 * Every surface that shows "what the project produces / is tracking" must
 * apply `excludeHse()`. Forgetting it causes:
 *   - HSE cards appearing in Impact tab "What This Project Produces"
 *   - HSE rows in the Performance Summary mission status list
 *   - Data Reliability scoring penalizing projects for not generating
 *     incident reports on a cadence (wrong semantic — we are NOT trying
 *     to encourage incidents)
 *   - HSE rows in Mission data entry grids (Action Center Data tab too)
 *   - HSE rows surfacing as "due/overdue" in the Action Center
 *
 * The HSE-specific surfaces (HSERow, IncidentDrillDown, EditIncidentModal,
 * the project's incident log) consume the OPPOSITE — they call `onlyHse()`.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  IF YOU'RE TEMPTED TO REFACTOR
 * ─────────────────────────────────────────────────────────────────────
 * Before proposing "let's give HSE its own category to remove these
 * filters," re-read the Product Principle above. The filters are the
 * ENFORCEMENT mechanism for the product decision, not technical debt.
 * The right move when adding a new mission consumer is to apply the
 * filter — not to remove it from existing consumers.
 */

/**
 * Minimum shape required for filtering. Any object with an optional
 * mission_type_key string field works (the engine result, the SubKpiInstance,
 * etc.). Type stays generic so callers don't have to widen.
 */
interface MissionSubkpiLike {
  mission_type_key?: string | null;
}

/**
 * Returns sub-KPIs that are ACTUAL Mission outputs (excludes HSE incident
 * trackers). Use on every surface that shows "what the project produces
 * or is tracking" — Impact tab, Mission Status, Data Reliability, etc.
 */
export function excludeHse<T extends MissionSubkpiLike>(subkpis: T[]): T[] {
  if (!Array.isArray(subkpis)) return [];
  return subkpis.filter((s) => s?.mission_type_key !== 'incident_count');
}

/**
 * Returns ONLY HSE incident tracker sub-KPIs. Used by HSERow + the HSE
 * pipeline (parallel to legacy Mission scoring).
 */
export function onlyHse<T extends MissionSubkpiLike>(subkpis: T[]): T[] {
  if (!Array.isArray(subkpis)) return [];
  return subkpis.filter((s) => s?.mission_type_key === 'incident_count');
}

/** Convenience predicate for use in inline filters / map keys / debug logs. */
export function isHseSubkpi(s: MissionSubkpiLike | null | undefined): boolean {
  return s?.mission_type_key === 'incident_count';
}
